/**
 * Backend Health Gate
 *
 * Waits for backend to be healthy before rendering children.
 * Shows a loading screen while the backend is starting up (useful in development).
 *
 * Authentication is handled by ProtectedRoute + Better Auth, not here.
 */

import { useBackendHealth } from '../hooks/useBackendHealth';

const isBypassAuth = import.meta.env.VITE_BYPASS_AUTH === 'true';

export function BackendHealthGate({ children }: { children: React.ReactNode }) {
  const { isBackendReady, isChecking, retryCount } = useBackendHealth();

  // Skip health check when auth is bypassed (UI preview mode)
  if (isBypassAuth) return <>{children}</>;

  if (!isBackendReady) {
    return (
      <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark flex items-center justify-center pt-safe">
        <div className="text-center px-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/30 mb-4">
            <svg
              className={`w-8 h-8 text-primary-600 dark:text-primary-400 ${isChecking ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-1">
            Connecting to server...
          </h2>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
            {retryCount > 0
              ? `Waiting for backend to start (attempt ${retryCount})`
              : 'Checking backend health'}
          </p>
        </div>
      </div>
    );
  }

  // Backend is ready - render children
  // Authentication is handled by ProtectedRoute, not here
  return <>{children}</>;
}
